import { useEffect, useState, useCallback } from "react";
import { fmtDate } from "@/components/DatePicker";
import api, { usd } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCompany } from "@/context/CompanyContext";
import { StatCard } from "@/components/StatCard";
import { ModuleUpgradeCard } from "@/components/ModuleUpgradeCard";
import { HRUpsellCard } from "@/components/HRUpsellCard";
import { useTileColors } from "@/lib/useTileColors";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Users, Wallet, CalendarClock, TrendingUp, Copy, Printer, Settings2 } from "lucide-react";
import { toast } from "sonner";
import { CompanyLogo } from "@/components/CompanyLogo";
import { KioskSettingsDialog } from "@/components/KioskSettingsDialog";
import { PunchLog } from "@/components/PunchLog";
import { KioskSyncReport } from "@/components/KioskSyncReport";
import { DocGateCard } from "@/components/DocGateCard";
import { I9DeadlineCard } from "@/components/I9DeadlineCard";
import { I9ReverifyCard } from "@/components/I9ReverifyCard";
import { DocExpiryCard } from "@/components/DocExpiryCard";
import { FollowUpInbox } from "@/components/FollowUpInbox";
import { ReplyInbox } from "@/components/ReplyInbox";
import { DigestCard } from "@/components/DigestCard";
import { WelcomeVideoCard } from "@/components/WelcomeVideoCard";
import { OvertimeBanner } from "@/components/OvertimeBanner";
import { ClientTaxDashboard } from "@/components/ClientTaxDashboard";
import { MyHealthCard } from "@/components/MyHealthCard";
import { ComplianceTimeline } from "@/components/ComplianceTimeline";
import { PayrollManagerCard } from "@/components/PayrollManagerCard";
import { useNavigate } from "react-router-dom";
import {
  ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";

const COLORS = ["#2563EB", "#059669", "#D97706", "#E11D48", "#7C3AED", "#0891B2"];

export default function CompanyDashboard() {
  const tc = useTileColors();
  const { user } = useAuth();
  const { activeCompanyId, refreshCompanies } = useCompany();
  const [data, setData] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(() => {
    const params = user.role === "bureau_admin" ? { company_id: activeCompanyId } : {};
    if (user.role === "bureau_admin" && !activeCompanyId) return;
    api.get("/dashboard/company", { params }).then((r) => setData(r.data)).catch(() => {});
  }, [user.role, activeCompanyId]);

  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="text-muted-foreground">Loading dashboard…</div>;

  return (
    <div className="space-y-6 animate-fade-up" data-testid="company-dashboard">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <CompanyLogo company={data.company} size="lg" />
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">{data.company?.name}</h1>
            <p className="text-sm text-muted-foreground mt-1">{data.company?.industry} · {data.company?.pay_schedule} payroll</p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button data-testid="copy-kiosk-link" variant="outline"
            onClick={() => {
              navigator.clipboard.writeText(`${window.location.origin}/timeclock/${data.company?.id}`);
              toast.success("Time clock link copied to clipboard");
            }}>
            <Copy className="w-4 h-4 mr-1.5" /> Copy Time Clock Link
          </Button>
          <Button data-testid="print-kiosk-kit" variant="outline" asChild>
            <a href={`/kiosk-kit/${data.company?.id}`} target="_blank" rel="noreferrer"><Printer className="w-4 h-4 mr-1.5" /> Print Kiosk Kit</a>
          </Button>
          <Button data-testid="open-kiosk-settings" onClick={() => setSettingsOpen(true)}>
            <Settings2 className="w-4 h-4 mr-1.5" /> Kiosk & Branding
          </Button>
        </div>
      </div>
      <KioskSettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} company={data.company}
        onSaved={() => { load(); refreshCompanies(); }} />

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard testid="metric-headcount" label="Active Employees" value={data.employee_count} icon={Users} color={tc.tile1} />
          <StatCard testid="metric-gross-ytd" label="Gross Payroll YTD" value={usd(data.total_gross_ytd)} icon={Wallet} color={tc.tile2} accent="text-emerald-600" />
          <StatCard testid="metric-runs" label="Payroll Runs" value={data.run_count} icon={TrendingUp} color={tc.tile3} accent="text-blue-600" />
          <div onClick={() => navigate("/change-requests")} className="cursor-pointer">
            <StatCard testid="metric-open-changes" label="Open Change Requests" value={data.open_change_requests} icon={CalendarClock} color={tc.tile5} accent="text-amber-600" sub={`${data.pending_leave} leave pending`} />
          </div>
        </div>
        <PayrollManagerCard pm={data.payroll_manager} />
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 p-5">
          <h3 className="font-semibold font-display mb-4">Payroll Runs — Gross vs Net</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="period" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" tickFormatter={(v) => fmtDate(v)} />
              <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => usd(v)} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
              <Legend />
              <Bar dataKey="gross" name="Gross" fill="#2563EB" radius={[6, 6, 0, 0]} />
              <Bar dataKey="net" name="Net" fill="#059669" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-4 sm:p-5">
          <h3 className="font-semibold font-display mb-4">Headcount by Department</h3>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={data.dept_breakdown} dataKey="count" nameKey="department" innerRadius={55} outerRadius={90} paddingAngle={2}>
                {data.dept_breakdown.map((e, i) => (<Cell key={i} fill={COLORS[i % COLORS.length]} />))}
              </Pie>
              <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <OvertimeBanner companyId={data.company?.id} isAdmin={user.role === "bureau_admin"} />
      {user.role === "hr_manager" && <HRUpsellCard />}
      <DocGateCard companyId={data.company?.id} isAdmin={user.role === "bureau_admin"} />
      <I9DeadlineCard companyId={data.company?.id} isAdmin={user.role === "bureau_admin"} />
      <I9ReverifyCard companyId={data.company?.id} isAdmin={user.role === "bureau_admin"} />
      <DocExpiryCard companyId={data.company?.id} isAdmin={user.role === "bureau_admin"} />
      <div className="grid lg:grid-cols-2 gap-4"><FollowUpInbox params={user.role === "bureau_admin" && data.company?.id ? { company_id: data.company.id } : {}} /><ReplyInbox params={user.role === "bureau_admin" && data.company?.id ? { company_id: data.company.id } : {}} /></div>
      <DigestCard companyId={data.company?.id} isAdmin={user.role === "bureau_admin"} />
      <WelcomeVideoCard companyId={data.company?.id} isAdmin={user.role === "bureau_admin"} />
      {user.role === "hr_manager" && <ModuleUpgradeCard />}
      {user.role === "hr_manager" && <MyHealthCard />}
      {user.role === "hr_manager" && <ClientTaxDashboard />}
      {user.role === "hr_manager" && <ComplianceTimeline />}
      <KioskSyncReport companyId={data.company?.id} />
      <PunchLog companyId={data.company?.id} />

      {data.last_run && (
        <Card className="p-4 sm:p-5">
          <h3 className="font-semibold font-display mb-3">Latest Payroll Run</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 font-mono-num">
            <div><div className="text-xs text-muted-foreground">Pay Date</div><div className="font-semibold">{fmtDate(data.last_run.pay_date)}</div></div>
            <div><div className="text-xs text-muted-foreground">Employees</div><div className="font-semibold">{data.last_run.employee_count}</div></div>
            <div><div className="text-xs text-muted-foreground">Gross</div><div className="font-semibold">{usd(data.last_run.gross_total)}</div></div>
            <div><div className="text-xs text-muted-foreground">Net</div><div className="font-semibold text-emerald-600">{usd(data.last_run.net_total)}</div></div>
          </div>
        </Card>
      )}
    </div>
  );
}
